import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { batchAPI } from '../../utils/api';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';

const Q_STATUS = { answered: '#28A745', skipped: '#F5A623', unanswered: 'var(--border)' };

export default function TestInterface() {
  const { batchId } = useParams();
  const nav = useNavigate();

  const [batch,      setBatch]      = useState(null);
  const [questions,  setQuestions]  = useState([]);
  const [answers,    setAnswers]    = useState({});  // { qId: 'A'|'B'|'C'|'D' }
  const [current,    setCurrent]    = useState(0);
  const [timeLeft,   setTimeLeft]   = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPanel,  setShowPanel]  = useState(false);
  const [startedAt,  setStartedAt]  = useState(null);
  const timerRef = useRef(null);
  const submittedRef = useRef(false);

  // ── Load test ────────────────────────────────────────────────
  useEffect(() => {
    batchAPI.start(batchId)
      .then(r => {
        setBatch(r.data.batch);
        setQuestions(r.data.questions);
        const dur = r.data.session?.duration_minutes || r.data.batch?.duration_minutes || 60;
        setTimeLeft(dur * 60);
        setStartedAt(Date.now());
        setLoading(false);
      })
      .catch(err => {
        const msg = err.response?.data?.error || 'Failed to load test';
        toast.error(msg);
        nav('/dashboard/tests');
      });
    return () => clearInterval(timerRef.current);
  }, [batchId]);

  // ── Countdown timer ──────────────────────────────────────────
  useEffect(() => {
    if (!batch || loading) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (!submittedRef.current) {
            toast('⏰ Time is up! Auto-submitting...', { icon:'⏰', duration:3000 });
            handleSubmit(true);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [batch, loading]);

  // ── Submit ───────────────────────────────────────────────────
  const handleSubmit = useCallback(async (autoSubmit = false) => {
    if (submittedRef.current) return;
    if (!autoSubmit) {
      const answered  = Object.keys(answers).length;
      const remaining = questions.length - answered;
      if (remaining > 0) {
        const ok = window.confirm(`You have ${remaining} unanswered questions. Submit anyway?`);
        if (!ok) return;
      }
    }
    submittedRef.current = true;
    clearInterval(timerRef.current);
    setSubmitting(true);

    const timeTaken = startedAt ? Math.floor((Date.now() - startedAt) / 60000) : 0;
    try {
      const r = await batchAPI.submit(batchId, { answers, timeTaken });
      const result = r.data.result;
      toast.success(r.data.message || 'Test submitted!');
      nav(`/dashboard/batch/${batchId}/result/${result.resultId}`, {
        state: { result: r.data.result, answers: r.data.answers }
      });
    } catch (err) {
      submittedRef.current = false;
      setSubmitting(false);
      toast.error(err.response?.data?.error || 'Submission failed');
    }
  }, [answers, questions, batchId, startedAt, nav]);

  // ── Helpers ──────────────────────────────────────────────────
  const selectAnswer = (qId, opt) => setAnswers(prev => ({ ...prev, [qId]: opt }));
  const markSkipped  = (qId) => setAnswers(prev => {
    const upd = { ...prev };
    delete upd[qId];
    return upd;
  });

  const fmtTime = (s) => {
    const m = Math.floor(s / 60), sec = s % 60;
    return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  };

  const getQStatus = (qId) => {
    if (answers[qId]) return 'answered';
    return 'unanswered';
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'var(--bg)', gap:16 }}>
      <div style={{ fontSize:40 }}>📝</div>
      <Spinner size={36} color="blue" />
      <p style={{ color:'var(--muted)', fontSize:14 }}>Loading your test...</p>
    </div>
  );

  if (submitting) return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'var(--bg)', gap:16 }}>
      <div style={{ fontSize:60 }}>📊</div>
      <Spinner size={36} color="blue" />
      <p style={{ fontFamily:'var(--font-heading)', fontSize:20, fontWeight:700 }}>Calculating your score...</p>
      <p style={{ color:'var(--muted)', fontSize:14 }}>Please wait</p>
    </div>
  );

  const q = questions[current];
  const answered  = Object.keys(answers).length;
  const unanswered = questions.length - answered;
  const timerDanger = timeLeft < 300; // < 5 mins

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column' }}>

      {/* ── Top Bar ── */}
      <div style={{
        background:'#fff', borderBottom:'2px solid var(--border)',
        padding:'10px 20px', display:'flex', alignItems:'center', gap:12,
        position:'sticky', top:0, zIndex:100,
      }}>
        {/* Logo / Batch */}
        <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:16, color:'var(--blue)', minWidth:0 }}>
          <span style={{ fontSize:20 }}>📝</span> Batch {batch?.batch_number}
          {batch?.batch_number === 100 && <span style={{ color:'#FFD700', marginLeft:4 }}>🏆</span>}
        </div>

        {/* Progress bar */}
        <div style={{ flex:1, maxWidth:300, display:'flex', flexDirection:'column', gap:3 }}>
          <div style={{ background:'var(--border)', borderRadius:99, height:6, overflow:'hidden' }}>
            <div style={{
              height:'100%', borderRadius:99,
              background:'linear-gradient(90deg,var(--blue),var(--blue-mid))',
              width:`${(answered/questions.length)*100}%`,
              transition:'width 0.3s',
            }} />
          </div>
          <div style={{ fontSize:11, color:'var(--muted)', textAlign:'center' }}>
            {answered}/{questions.length} answered
          </div>
        </div>

        {/* Timer */}
        <div style={{
          background: timerDanger ? '#dc3545' : 'var(--blue)',
          color:'#fff', borderRadius:10, padding:'6px 16px',
          fontFamily:'var(--font-heading)', fontWeight:900, fontSize:20,
          minWidth:90, textAlign:'center',
          boxShadow: timerDanger ? '0 0 0 3px rgba(220,53,69,0.3)' : 'none',
        }}>
          ⏱ {fmtTime(timeLeft)}
        </div>

        {/* Question navigator toggle */}
        <button onClick={() => setShowPanel(!showPanel)}
          style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'7px 12px', cursor:'pointer', fontSize:13, fontWeight:600 }}>
          {showPanel ? '✕ Hide' : '☰ Nav'}
        </button>

        {/* Submit */}
        <button className="btn btn-primary" style={{ fontSize:13, padding:'8px 18px', whiteSpace:'nowrap' }}
          onClick={() => handleSubmit(false)}>
          Submit ✓
        </button>
      </div>

      <div style={{ display:'flex', flex:1, maxWidth:1100, margin:'0 auto', width:'100%', gap:0 }}>

        {/* ── Question Panel ── */}
        <div style={{ flex:1, padding:'24px 20px', minWidth:0 }}>

          {/* Question card */}
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid var(--border)', padding:'24px 26px', marginBottom:16 }}>
            <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:20 }}>
              <div style={{
                background:'linear-gradient(135deg,var(--blue),var(--blue-mid))', color:'#fff',
                borderRadius:10, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center',
                fontFamily:'var(--font-heading)', fontWeight:900, fontSize:15, flexShrink:0,
              }}>
                {current + 1}
              </div>
              <p style={{ fontSize:16, lineHeight:1.7, color:'var(--text)', margin:0, fontWeight:500 }}>
                {q.question_text}
              </p>
            </div>

            {/* Options */}
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {['A','B','C','D'].map(opt => {
                const optText = q[`option_${opt.toLowerCase()}`];
                const selected = answers[q.id] === opt;
                return (
                  <button key={opt} onClick={() => selectAnswer(q.id, opt)}
                    style={{
                      display:'flex', alignItems:'center', gap:12,
                      padding:'12px 16px', borderRadius:12, border:'none',
                      background: selected ? 'linear-gradient(135deg,var(--blue),var(--blue-mid))' : 'var(--bg)',
                      color: selected ? '#fff' : 'var(--text)',
                      cursor:'pointer', textAlign:'left',
                      outline: selected ? 'none' : '1.5px solid var(--border)',
                      fontFamily:'var(--font-body)', fontSize:14, lineHeight:1.5,
                      transition:'all 0.15s',
                    }}>
                    <span style={{
                      width:28, height:28, borderRadius:'50%', flexShrink:0,
                      background: selected ? 'rgba(255,255,255,0.25)' : 'var(--border)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontWeight:700, fontSize:13,
                    }}>{opt}</span>
                    {optText}
                  </button>
                );
              })}
            </div>

            {/* Skip / Clear */}
            <div style={{ display:'flex', gap:10, marginTop:14 }}>
              {answers[q.id] && (
                <button onClick={() => markSkipped(q.id)}
                  style={{ background:'none', border:'1px solid var(--border)', borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', color:'var(--muted)' }}>
                  Clear Answer
                </button>
              )}
            </div>
          </div>

          {/* Prev / Next */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <button className="btn btn-outline" disabled={current === 0}
              onClick={() => setCurrent(c => c - 1)}
              style={{ opacity: current===0 ? 0.4:1 }}>
              ← Previous
            </button>
            <span style={{ color:'var(--muted)', fontSize:13 }}>
              {current + 1} of {questions.length}
            </span>
            {current < questions.length - 1 ? (
              <button className="btn btn-primary" onClick={() => setCurrent(c => c + 1)}>
                Next →
              </button>
            ) : (
              <button className="btn btn-primary" style={{ background:'#28A745', borderColor:'#28A745' }}
                onClick={() => handleSubmit(false)}>
                Submit ✓
              </button>
            )}
          </div>
        </div>

        {/* ── Question Navigator Panel ── */}
        {showPanel && (
          <div style={{
            width:260, background:'#fff', borderLeft:'1px solid var(--border)',
            padding:16, overflowY:'auto', maxHeight:'calc(100vh - 58px)',
            position:'sticky', top:58,
          }}>
            <div style={{ fontFamily:'var(--font-heading)', fontWeight:700, fontSize:14, marginBottom:12 }}>
              Questions Navigator
            </div>

            {/* Legend */}
            <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
              {[['answered','Answered','#28A745'],['unanswered','Not Yet','var(--border)']].map(([s,l,c]) => (
                <div key={s} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11 }}>
                  <div style={{ width:12, height:12, borderRadius:3, background:c }} />
                  <span style={{ color:'var(--muted)' }}>{l}</span>
                </div>
              ))}
            </div>

            {/* Grid of question numbers */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:5 }}>
              {questions.map((qq, idx) => {
                const status = getQStatus(qq.id);
                const isCurrent = idx === current;
                return (
                  <button key={qq.id} onClick={() => { setCurrent(idx); }}
                    style={{
                      width:34, height:34, borderRadius:8,
                      border:`2px solid ${isCurrent ? 'var(--blue)' : 'transparent'}`,
                      background: status === 'answered' ? '#28A745' : 'var(--bg)',
                      color: status === 'answered' ? '#fff' : 'var(--muted)',
                      fontWeight: isCurrent ? 900 : 600,
                      fontSize:12, cursor:'pointer',
                      outline: isCurrent ? '2px solid rgba(26,60,143,0.3)' : 'none',
                    }}>
                    {idx + 1}
                  </button>
                );
              })}
            </div>

            {/* Summary */}
            <div style={{ marginTop:20, padding:14, background:'var(--bg)', borderRadius:12 }}>
              <div style={{ fontSize:13, display:'flex', flexDirection:'column', gap:6 }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ color:'var(--muted)' }}>Total</span>
                  <strong>{questions.length}</strong>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ color:'#28A745' }}>Answered</span>
                  <strong style={{ color:'#28A745' }}>{answered}</strong>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ color:'var(--muted)' }}>Remaining</span>
                  <strong>{unanswered}</strong>
                </div>
              </div>
              <button className="btn btn-primary" style={{ width:'100%', marginTop:14, fontSize:13 }}
                onClick={() => handleSubmit(false)}>
                Submit Test ✓
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
